import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';
import { motion } from 'framer-motion';

import Demo, {
  DemoToggleButtonGroupControl,
  DemoSliderControl,
} from '@/components/Demo';
import Paper from '@/components/Paper';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';
import ToggleButton from '@/components/ToggleButton';

import { CODE_BACKGROUND } from './index.constants';
import {
  getInitialPlaceItems,
  constructSnippet,
} from './GridStack.helpers';

const IMAGES = [
  {
    src: 'photo-stack-rainbow.jpg',
    alt: 'A small toy rainbow sits on a table',
    width: 165,
    height: 165,
    transform: { rotate: -3 },
  },
  {
    src: 'photo-stack-droplets-rect.jpg',
    alt: 'A bunch of iridescent water droplets on black',
    width: 160,
    height: 200,
    transform: { rotate: 2, y: -3 },
  },
  {
    src: 'photo-stack-city-rect.jpg',
    alt: 'A pink city skyline',
    width: 370 / 2,
    height: 300 / 2,
    transform: { rotate: -4, y: -1 },
  },
  {
    src: 'photo-stack-peace-rect.jpg',
    alt: 'A neon peace sign',
    width: 300 / 2,
    height: 320 / 2,
    transform: { rotate: 0.5, y: 2 },
  },
];

function GridStack({ variant, codeOutput }) {
  const [isGridRevealed, setIsGridRevealed] = React.useState(
    variant !== 'initial'
  );

  return (
    <Demo
      initialValues={{
        numOfItems: variant === 'toggleable-place-items' ? 4 : 1,
        placeItems: getInitialPlaceItems(variant),
      }}
      controlPosition="top"
      customGrid={{
        gridTemplateColumns: '1fr',
      }}
      outputWrapper={null}
      controls={[
        variant === 'toggleable-place-items' ? (
          <DemoToggleButtonGroupControl
            key="placeItems"
            id="placeItems"
            label="place-items"
            options={[
              { label: 'start (default)', value: 'start' },
              { label: 'center', value: 'center' },
            ]}
          />
        ) : (
          <DemoSliderControl
            key="numOfItems"
            id="numOfItems"
            label="# of items"
            min={0}
            max={4}
          />
        ),
      ]}
    >
      {({ numOfItems, placeItems }) => {
        const images =
          variant === 'toggleable-place-items'
            ? IMAGES.filter((_, index) => index < numOfItems)
            : IMAGES;

        const code = constructSnippet(variant, placeItems);

        return (
          <div>
            <Row>
              <Column>
                <CodeSnippetProvider lang="css" code={code}>
                  {codeOutput}
                </CodeSnippetProvider>
              </Column>
              <DemoColumn>
                <Container
                  style={{
                    placeItems,
                  }}
                >
                  {variant !== 'initial' && (
                    <GridOutline
                      isRevealed={isGridRevealed}
                      appliedElements={IMAGES.slice(0, numOfItems)}
                    />
                  )}
                  {images.map((image, index) => {
                    const imageNum = index + 1;
                    const FloatingImage =
                      variant === 'toggleable-place-items'
                        ? FloatingImageMotion
                        : FloatingImageSpring;
                    return (
                      <FloatingImage
                        key={image.src}
                        isVisible={imageNum <= numOfItems}
                        variant={variant}
                        {...image}
                      />
                    );
                  })}
                </Container>
              </DemoColumn>
            </Row>
            {variant !== 'initial' && (
              <Footer>
                <ToggleButton
                  active={isGridRevealed}
                  handleToggle={() =>
                    setIsGridRevealed(!isGridRevealed)
                  }
                >
                  Reveal Grid
                </ToggleButton>
              </Footer>
            )}
          </div>
        );
      }}
    </Demo>
  );
}

function FloatingImageSpring({
  isVisible,
  variant,
  src,
  alt,
  width,
  height,
  transform,
}) {
  const style = useSpring({
    width: variant === 'initial' ? 150 : width,
    height: variant === 'initial' ? 150 : height,
    transform: isVisible
      ? `rotate(${
          variant === 'initial' ? transform.rotate : 0
        }deg) translateY(${
          variant === 'initial' ? transform.y || 0 : 0
        }px)`
      : `rotate(0deg) translateY(10px)`,
    config: {
      tension: 200,
      friction: 50,
    },
  });

  return (
    <Child style={{ ...style, opacity: isVisible ? 1 : 0 }}>
      <Image src={`/images/center-a-div/${src}`} alt={alt} />
      <Gradient />
      <Label>.element</Label>
    </Child>
  );
}

function FloatingImageMotion({ src, alt, width, height }) {
  return (
    <Child
      as={motion.div}
      layout="position"
      style={{ width, height }}
    >
      <Image src={`/images/center-a-div/${src}`} alt={alt} />
      <Gradient />
      <Label>.element</Label>
    </Child>
  );
}

function GridOutline({ isRevealed, appliedElements }) {
  const maxHeight = appliedElements.reduce(
    (acc, elem) => (elem.height > acc ? elem.height : acc),
    0
  );
  const maxWidth = appliedElements.reduce(
    (acc, elem) => (elem.width > acc ? elem.width : acc),
    0
  );

  return (
    <>
      <GridRow
        animate={{ height: maxHeight ? maxHeight + 6 : '100%' }}
        style={{
          opacity: isRevealed && appliedElements.length ? 1 : 0,
        }}
      />
      <GridColumn
        animate={{ width: maxWidth ? maxWidth + 6 : '100%' }}
        style={{
          opacity: isRevealed && appliedElements.length ? 1 : 0,
        }}
      />
    </>
  );
}

const Row = styled.div`
  display: flex;
  gap: 24px;
  padding-bottom: 16px;

  @media (max-width: 34rem) {
    flex-direction: column-reverse;
  }
`;
const Column = styled.div`
  position: relative;
  flex: 1;
  background: ${CODE_BACKGROUND};
  border-radius: 8px;
`;

const DemoColumn = styled(Column)`
  display: flex;
  justify-content: center;
  min-height: 312px;

  @media (max-width: 34rem) {
    min-height: revert;
  }
`;

const Container = styled(Paper)`
  width: 100%;
  height: 100%;
  position: relative;
  display: grid;
  place-content: center;
  padding: 16px 0px;

  @media (max-width: 34rem) {
    height: 290px;
  }
`;

const Child = styled(animated.div)`
  grid-area: 1 / 1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 150px;
  height: 150px;
  border: 3px solid white;
  border-radius: 6px;
  overflow: hidden;
  overflow: clip;
  box-shadow:
    0px 1px 2px hsl(0deg 0% 0% / 0.2),
    0px 8px 16px hsl(0deg 0% 0% / 0.2);
  will-change: transform;
  transition: opacity 300ms cubic-bezier(0.3, 0.59, 0.48, 0.98);
`;

const Image = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Gradient = styled.div`
  position: absolute;
  inset: auto 0 0 0;
  width: 100%;
  height: 70%;
  background: linear-gradient(
    to bottom,
    hsl(0 0% 0% / 0) 0%,
    hsl(0 0% 0% / 0.013) 8.1%,
    hsl(0 0% 0% / 0.049) 15.5%,
    hsl(0 0% 0% / 0.104) 22.5%,
    hsl(0 0% 0% / 0.175) 29%,
    hsl(0 0% 0% / 0.259) 35.3%,
    hsl(0 0% 0% / 0.352) 41.2%,
    hsl(0 0% 0% / 0.45) 47.1%,
    hsl(0 0% 0% / 0.55) 52.9%,
    hsl(0 0% 0% / 0.648) 58.8%,
    hsl(0 0% 0% / 0.741) 64.7%,
    hsl(0 0% 0% / 0.825) 71%,
    hsl(0 0% 0% / 0.896) 77.5%,
    hsl(0 0% 0% / 0.951) 84.5%,
    hsl(0 0% 0% / 0.987) 91.9%,
    hsl(0 0% 0%) 100%
  );
  opacity: 0.75;
`;

const Label = styled.span`
  position: absolute;
  inset: auto 0 0 0;
  height: 25%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-weight: var(--font-weight-medium);
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
`;

const GridTrack = styled(motion.div)`
  --border: 3px dashed hsl(345deg 100% 50%);
  position: absolute;
  margin: auto;
  transition: opacity 500ms;
`;

const GridRow = styled(GridTrack)`
  inset: 0 auto;
  width: 100%;
  border-top: var(--border);
  border-bottom: var(--border);
`;

const GridColumn = styled(GridTrack)`
  inset: auto 0;
  height: 100%;
  border-left: var(--border);
  border-right: var(--border);
`;

export default GridStack;
